import { Action } from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { useGroupByUuidQuery } from "@/common/api/group/use-group-by-uuid.query";
import { generateUuidV4Regex } from "@/common/helpers/utils";
import { Loader, LoaderSize } from "@/components/ui/loader";
import { NotFoundView } from "@/views/common/notfound.view";
import type { ComponentType } from "react";
import { Redirect, Route, Switch, useParams } from "wouter";
import { AppRoleRoutes } from "./app-role/app-role.routes";
import { GroupCreateView } from "./group-create.view";
import { GroupUuidUpdateView } from "./group-uuid-update.view";
import { GroupUuidView } from "./group-uuid.view";
import { GroupView } from "./group.view";
import { RuleRoutes } from "./rule/rule.routes";
import { UserRoutes } from "./user/user.routes";

const ActiveGroupAppRoleRoutes = requireActiveGroup(AppRoleRoutes);
const ActiveGroupRuleRoutes = requireActiveGroup(RuleRoutes);
const ActiveGroupUserRoutes = requireActiveGroup(UserRoutes);
const ActiveGroupUpdateView = requireActiveGroup(GroupUuidUpdateView);

export function GroupRoutes() {
  const ability = useAbility();
  const canManageGroup = ability.can(Action.Manage, "Group");
  const canManageRules = ability.can(Action.Manage, "Rule");

  const groupUuidAppRoleRegex = generateUuidV4Regex({
    paramName: "groupUuid",
    suffix: "/app-role",
    allowTrailing: true,
  });
  const groupUuidRuleRegex = generateUuidV4Regex({
    paramName: "groupUuid",
    suffix: "/rule",
    allowTrailing: true,
  });
  const groupUuidUserRegex = generateUuidV4Regex({
    paramName: "groupUuid",
    suffix: "/user",
    allowTrailing: true,
  });
  const groupUuidUpdateRegex = generateUuidV4Regex({ paramName: "groupUuid", suffix: "/update" });
  const groupUuidRegex = generateUuidV4Regex({ paramName: "groupUuid" });

  return (
    <Switch>
      <Route
        nest
        path={groupUuidAppRoleRegex}
        component={canManageGroup ? ActiveGroupAppRoleRoutes : RedirectToGroupDetail}
      />
      <Route
        nest
        path={groupUuidRuleRegex}
        component={canManageRules ? ActiveGroupRuleRoutes : RedirectToGroupDetail}
      />
      <Route
        nest
        path={groupUuidUserRegex}
        component={canManageGroup ? ActiveGroupUserRoutes : RedirectToGroupDetail}
      />
      <Route path={"/"} component={GroupView} />
      <Route path={"/create"} component={canManageGroup ? GroupCreateView : RedirectToGroupList} />
      <Route path={groupUuidUpdateRegex} component={canManageGroup ? ActiveGroupUpdateView : RedirectToGroupDetail} />
      <Route path={groupUuidRegex} component={GroupUuidView} />
      <Route component={NotFoundView} />
    </Switch>
  );
}

function RedirectToGroupList() {
  return <Redirect to="~/ram/group" replace />;
}

function RedirectToGroupDetail() {
  const params = useParams<{ groupUuid: string }>();
  return <Redirect to={`~/ram/group/${params.groupUuid}`} replace />;
}

function requireActiveGroup(Component: ComponentType): ComponentType {
  return function ActiveGroupRoute() {
    const params = useParams<{ groupUuid: string }>();
    const { data: group, isPending } = useGroupByUuidQuery(params.groupUuid ?? "");

    if (isPending) {
      return (
        <div className="flex min-h-100 items-center justify-center">
          <Loader size={LoaderSize.Medium} />
        </div>
      );
    }

    if (!group || group.deletedAt) {
      return <RedirectToGroupDetail />;
    }

    return <Component />;
  };
}
